import { ArrowUpRight } from "lucide-react";
import JobCard from "../components/careers/JobCard";
import KeweenawMosaic from "../components/careers/KeweenawMosaic";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import { ButtonLink } from "../components/ui/Button";
import ExternalLink from "../components/ui/ExternalLink";
import SectionHeading from "../components/ui/SectionHeading";
import {
  benefits,
  careersEmail,
  careersMailto,
  careersPage,
  coreValues,
  openPositions,
  perks,
} from "../data/careers";
import { images } from "../data/images";

const { values, positions, keweenaw, cta } = careersPage;

function Values() {
  return (
    <section className="bg-white px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading
          eyebrow={values.eyebrow}
          title={values.title}
          className="mb-11"
        >
          <p>{values.text}</p>
        </SectionHeading>
        <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {coreValues.map(({ title, text, quote }, i) => (
            <li
              key={title}
              className="rounded-b-brand border-t-4 border-yellow bg-surface px-6 pt-6 pb-7"
            >
              <span className="tag text-blue-deep" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display mt-2 mb-2.5 text-2xl text-slate uppercase">
                {title}
              </h3>
              <p className="text-[15.5px] text-muted">
                {text}
                {quote && (
                  <>
                    {" "}
                    <em className="font-bold text-slate">{quote}</em>
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Positions() {
  return (
    <section id="openings" className="bg-surface px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading
          eyebrow={positions.eyebrow}
          title={positions.title}
          className="mb-10"
        >
          <p>{openPositions.length ? positions.text : positions.none}</p>
        </SectionHeading>
        {openPositions.length > 0 ? (
          <div className="flex flex-col gap-5">
            {openPositions.map((position) => (
              <JobCard key={position.title} position={position} />
            ))}
          </div>
        ) : (
          <ButtonLink href={careersMailto("Introducing myself")}>
            Introduce yourself
          </ButtonLink>
        )}
        <p className="mt-8 rounded-brand border border-dashed border-line-strong bg-white/60 px-6 py-5 text-center text-[15.5px] text-muted">
          {positions.noFit}{" "}
          <a
            href={careersMailto("Introducing myself")}
            className="font-bold text-blue-deep underline decoration-yellow decoration-2 underline-offset-4 hover:text-ink"
          >
            {careersEmail}
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function BenefitGrid({ items, label, as: Heading = "h3" }) {
  return (
    <ul
      aria-label={label}
      className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
    >
      {items.map(({ title, text, icon: Icon }) => (
        <li
          key={title}
          className="flex items-start gap-4 rounded-brand border border-line bg-surface px-6 py-5"
        >
          <span className="grid size-10 flex-none place-items-center rounded-lg bg-yellow text-ink">
            <Icon size={20} strokeWidth={2} aria-hidden="true" />
          </span>
          <div>
            <Heading className="font-display text-[19px] text-slate uppercase">
              {title}
            </Heading>
            <p className="mt-1 text-[15px] text-muted">{text}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Benefits() {
  const { benefits: copy } = careersPage;
  return (
    <section id="benefits" className="bg-white px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <SectionHeading
          eyebrow={copy.eyebrow}
          title={copy.title}
          className="mb-11"
        >
          <p>{copy.text}</p>
        </SectionHeading>
        <BenefitGrid items={benefits} label="Benefits" />
        <h3
          id="perks"
          className="tag mt-12 mb-5 flex items-center gap-4 text-[13px] text-blue-deep"
        >
          {copy.perksTitle}
          <span aria-hidden="true" className="h-px flex-1 bg-line" />
        </h3>
        <BenefitGrid items={perks} label={copy.perksTitle} as="h4" />
      </div>
    </section>
  );
}

function Keweenaw() {
  return (
    <section
      id="keweenaw"
      className="blueprint px-4 py-20 text-white sm:px-6 md:py-24"
    >
      <div className="mx-auto max-w-280">
        <div className="grid grid-cols-1 items-start gap-x-14 gap-y-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <SectionHeading
            dark
            eyebrow={keweenaw.eyebrow}
            title={keweenaw.title}
          />
          <div className="text-[16.5px] text-white/85">
            {keweenaw.text.map((text) => (
              <p key={text.slice(0, 24)} className="mb-4">
                {text}
              </p>
            ))}
            <ExternalLink
              href={keweenaw.url}
              className="inline-flex items-center gap-1.5 border-b-2 border-yellow pb-0.5 font-bold text-white transition-colors hover:text-yellow"
            >
              {keweenaw.link}
              <ArrowUpRight size={17} aria-hidden="true" />
              <span className="sr-only">(opens in a new tab)</span>
            </ExternalLink>
          </div>
        </div>
        <KeweenawMosaic className="mt-12" />
      </div>
    </section>
  );
}

export default function CareersPage() {
  return (
    <>
      <PageHero
        eyebrow={careersPage.eyebrow}
        title={careersPage.title}
        lede={careersPage.lede}
        image={images.teamGroup}
        imageAlt={careersPage.heroImageAlt}
      >
        <div className="flex flex-wrap gap-3.5">
          <ButtonLink href="#openings">
            {openPositions.length
              ? `See open positions (${openPositions.length})`
              : "See open positions"}
          </ButtonLink>
          <ButtonLink href="#benefits" variant="glass">
            Benefits &amp; perks ↓
          </ButtonLink>
        </div>
      </PageHero>
      <Values />
      <Positions />
      <Benefits />
      <Keweenaw />
      <CtaPanel
        eyebrow={cta.eyebrow}
        title={cta.title}
        text={cta.text}
        href={
          openPositions.length
            ? "#openings"
            : careersMailto("Introducing myself")
        }
        label={
          openPositions.length ? "See open positions" : "Introduce yourself"
        }
        className="bg-white"
      />
    </>
  );
}
